/**
 * Search Management - Moon Moon Agency
 * Handles search overlay functionality
 */

/*********************************************************************************************
 * 🔍 SEARCH MODULE
 */
const SearchManager = {
	// Local configuration
	config: {
		selectors: {
			searchContainer: '[data-search-container]',
			searchInput: '[data-search-input]',
			searchInputHeader: '[data-search-input-header]',
			searchToggle: '[data-search-toggle]',
			predictiveSearch: '[data-predictive-search]',
			predictiveSearchDefault: '#predictive-search-default'
		},
		classes: {
			stopScrolling: 'stop-scrolling',
			open: 'mm-open'
		},
		timing: {
			searchFocusDelay: 100,
			searchBlurDelay: 100
		}
	},

	// Focus trap state
	focusTrap: {
		isActive: false,
		triggerElement: null,
		firstFocusable: null,
		lastFocusable: null,
		handleKeyDown: null
	},

	/**
	 * Setup focus trap for search overlay
	 */
	setupFocusTrap(searchContainer, triggerElement) {
		const focusableElements = searchContainer.querySelectorAll(
			'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'
		);

		if (focusableElements.length === 0) return;

		this.focusTrap.triggerElement = triggerElement;
		this.focusTrap.firstFocusable = focusableElements[0];
		this.focusTrap.lastFocusable = focusableElements[focusableElements.length - 1];

		// Handle Tab and Escape keys
		this.focusTrap.handleKeyDown = (e) => {
			// Escape key closes search
			if (e.key === 'Escape' || e.key === 'Esc') {
				e.preventDefault();
				this.hide();
				return;
			}

			// Tab key traps focus
			if (e.key !== 'Tab') return;

			if (e.shiftKey) {
				// Shift + Tab: going backwards
				if (document.activeElement === this.focusTrap.firstFocusable) {
					e.preventDefault();
					this.focusTrap.lastFocusable?.focus();
				}
			} else {
				// Tab: going forwards
				if (document.activeElement === this.focusTrap.lastFocusable) {
					e.preventDefault();
					this.focusTrap.firstFocusable?.focus();
				}
			}
		};

		document.addEventListener('keydown', this.focusTrap.handleKeyDown);
		this.focusTrap.isActive = true;

		window.log('info', 'SearchManager', 'Focus trap activated');
	},

	/**
	 * Remove focus trap
	 */
	removeFocusTrap() {
		if (this.focusTrap.isActive && this.focusTrap.handleKeyDown) {
			document.removeEventListener('keydown', this.focusTrap.handleKeyDown);
			this.focusTrap.isActive = false;
			this.focusTrap.triggerElement = null;
			this.focusTrap.firstFocusable = null;
			this.focusTrap.lastFocusable = null;
			this.focusTrap.handleKeyDown = null;
			window.log('info', 'SearchManager', 'Focus trap removed');
		}
	},

	/**
	 * Show search overlay
	 */
	show(triggerElement) {
		// Find the visible search container (mobile or desktop)
		let searchContainer = null;
		const allContainers = document.querySelectorAll(this.config.selectors.searchContainer);

		// If on mobile, prefer the container in .mm-mobile, otherwise use the first one
		if (window.innerWidth <= 768) {
			for (const container of allContainers) {
				if (container.closest('.mm-mobile') || !container.closest('.mm-desktop')) {
					searchContainer = container;
					break;
				}
			}
		}

		// If not found or on desktop, use the first container
		if (!searchContainer && allContainers.length > 0) {
			searchContainer = allContainers[0];
		}

		const elements = {
			search: searchContainer || window.getElement(this.config.selectors.searchContainer),
			html: document.documentElement,
			searchInput: window.getElement(this.config.selectors.searchInput),
			searchInputHeader: window.getElement(this.config.selectors.searchInputHeader),
			predictiveSearchDefault: searchContainer ? searchContainer.querySelector(this.config.selectors.predictiveSearchDefault) : document.querySelector(this.config.selectors.predictiveSearchDefault)
		};

		if (!elements.search || !elements.html) {
			window.log('warn', 'SearchManager', 'Required elements not found');
			return;
		}

		const { search, html, searchInput, searchInputHeader, predictiveSearchDefault } = elements;

		html.classList.add(this.config.classes.stopScrolling);
		search.classList.add(this.config.classes.open);
		search.setAttribute('aria-hidden', 'false');

		// Click on backdrop (outside inner panel) closes the modal
		if (!search._mmBackdropHandler) {
			search._mmBackdropHandler = () => this.hide();
			search.addEventListener('click', search._mmBackdropHandler);
		}

		// Click inside inner panel stops propagation to prevent backdrop handler from firing
		const inner = search.querySelector('[data-search-modal-inner]');
		if (inner && !inner._mmInnerClickHandler) {
			inner._mmInnerClickHandler = (e) => e.stopPropagation();
			inner.addEventListener('click', inner._mmInnerClickHandler);
		}

		// Get the input in the overlay (by ID or selector)
		const overlayInput = searchInput || document.getElementById('Search');

		// Sync input values
		if (searchInputHeader && overlayInput) {
			overlayInput.value = searchInputHeader.value;
		}

		// Show default content if input is empty (always show on open if empty)
		// Force show default content when overlay opens
		if (predictiveSearchDefault) {
			const inputValue = overlayInput ? overlayInput.value.trim() : '';
			if (inputValue === '') {
				predictiveSearchDefault.style.opacity = '0';
				predictiveSearchDefault.classList.remove('mm-none');
				predictiveSearchDefault.style.display = 'flex';
				predictiveSearchDefault.style.visibility = 'visible';
				requestAnimationFrame(() => {
					predictiveSearchDefault.style.opacity = '1';
				});
			} else {
				predictiveSearchDefault.style.opacity = '';
				predictiveSearchDefault.classList.add('mm-none');
				predictiveSearchDefault.style.display = 'none';
			}
		}

		// Initialize mobile splides when overlay opens
		if (window.innerWidth <= 768) {
			// Wait for DOM to be ready and ensure content is visible
			// Use requestAnimationFrame to ensure layout is complete
			requestAnimationFrame(() => {
				setTimeout(() => {
					// Find splides in the visible search container
					const visibleContainer = search;
					if (!visibleContainer) return;

					// Ensure container is visible before initializing
					const isVisible = visibleContainer.offsetWidth > 0 && visibleContainer.offsetHeight > 0;
					if (!isVisible) {
						if (window.log) {
							window.log('warn', 'SearchManager', 'Search container not visible yet');
						}
						// Retry after a short delay
						setTimeout(() => {
							if (window.SplideManager) {
								window.SplideManager.init();
							}
						}, 100);
						return;
					}

					const splides = visibleContainer.querySelectorAll('.splide');

					if (splides.length === 0) {
						if (window.log) {
							window.log('warn', 'SearchManager', 'No splides found in search container');
						}
						return;
					}

					// Check if Splide is available
					const SplideClass = typeof Splide !== 'undefined' ? Splide : (typeof window !== 'undefined' && window.Splide ? window.Splide : null);

					if (!SplideClass) {
						if (window.log) {
							window.log('warn', 'SearchManager', 'Splide class not available');
						}
						// Fallback to SplideManager
						if (window.SplideManager) {
							window.SplideManager.init();
						}
						return;
					}

					splides.forEach(splide => {
						// Destroy existing instance if any
						if (splide._splide) {
							try {
								splide._splide.destroy();
							} catch (e) {
								// Ignore errors
							}
							delete splide._splide;
						}

						// Initialize new instance
						try {
							const configData = splide.dataset.splide;
							let instance;
							if (configData) {
								// Clean and parse JSON (same as SplideManager)
								const cleanedData = configData
									.replace(/'/g, '"')
									.replace(/(\w+):/g, '"$1":');
								const config = JSON.parse(cleanedData);
								instance = new SplideClass(splide, config).mount();
							} else {
								instance = new SplideClass(splide).mount();
							}

							splide._splide = instance;

							// Force refresh after a short delay to ensure layout is complete
							setTimeout(() => {
								if (instance && typeof instance.refresh === 'function') {
									instance.refresh();
								}
							}, 50);

							// Refresh after images load (same as SplideManager)
							const images = splide.querySelectorAll('img');
							if (images.length > 0) {
								let loadedCount = 0;
								const totalImages = images.length;
								images.forEach(img => {
									if (img.complete) {
										loadedCount++;
									} else {
										img.addEventListener('load', () => {
											loadedCount++;
											if (loadedCount === totalImages && instance) {
												instance.refresh();
											}
										});
									}
								});
								if (loadedCount === totalImages && instance) {
									instance.refresh();
								}
							}
						} catch (e) {
							if (window.log) {
								window.log('error', 'SearchManager', `Splide initialization error: ${e.message}`);
							}
							// Try fallback initialization
							try {
								splide._splide = new SplideClass(splide).mount();
							} catch (fallbackError) {
								if (window.log) {
									window.log('error', 'SearchManager', `Splide fallback initialization failed: ${fallbackError.message}`);
								}
							}
						}
					});
				}, 200);
			});
		}


		setTimeout(() => {
			const overlayInput = searchInput || document.getElementById('Search');
			if (overlayInput) {
				overlayInput.focus();
			}
			// Setup focus trap after search is shown
			this.setupFocusTrap(search, triggerElement);
		}, this.config.timing.searchFocusDelay);

		// Touch handling for mobile
		const predictiveSearch = window.getElement(this.config.selectors.predictiveSearch);
		if (predictiveSearch) {
			predictiveSearch.addEventListener('touchstart', () => {
				if (searchInput) {
					setTimeout(() => searchInput.blur(), this.config.timing.searchBlurDelay);
				}
			});
		}

		window.emit('global:search-opened');
	},

	/**
	 * Hide search overlay
	 */
	hide(keepFilterActive = false, keepStopScrolling = false) {
		const elements = {
			search: window.getElement(this.config.selectors.searchContainer),
			html: document.documentElement,
			searchInput: window.getElement(this.config.selectors.searchInput),
			searchInputHeader: window.getElement(this.config.selectors.searchInputHeader),
			predictiveSearchDefault: document.querySelector(this.config.selectors.predictiveSearchDefault)
		};

		if (!elements.search || !elements.html) {
			window.log('warn', 'SearchManager', 'Required elements not found for hiding');
			return;
		}

		const { search, html, searchInput, searchInputHeader, predictiveSearchDefault } = elements;

		if (!keepStopScrolling) {
			html.classList.remove(this.config.classes.stopScrolling);
		}
		search.classList.remove(this.config.classes.open);
		search.setAttribute('aria-hidden', 'true');

		// Remove backdrop click handler
		if (search._mmBackdropHandler) {
			search.removeEventListener('click', search._mmBackdropHandler);
			delete search._mmBackdropHandler;
		}

		// Remove inner panel click handler
		const inner = search.querySelector('[data-search-modal-inner]');
		if (inner?._mmInnerClickHandler) {
			inner.removeEventListener('click', inner._mmInnerClickHandler);
			delete inner._mmInnerClickHandler;
		}

		// Remove focus trap when search closes
		const trigger = this.focusTrap.triggerElement;
		this.removeFocusTrap();
		if (trigger && search.contains(document.activeElement)) trigger.focus({ preventScroll: true });

		// Use NavigationManager for background filter
		if (!keepFilterActive && window.NavigationManager?.hideBackgroundFilter) {
			window.NavigationManager.hideBackgroundFilter();
		}

		// Clear inputs silently (no dispatchEvent — avoids triggering visual state changes mid-animation)
		if (searchInput) searchInput.value = '';
		if (searchInputHeader) searchInputHeader.value = '';

		// Cancel any in-flight predictive fetch without touching the DOM
		const mmPredSearch = search.querySelector('mm-predictive-search');
		if (mmPredSearch?._abort) {
			mmPredSearch._abort.abort();
			mmPredSearch._abort = null;
		}

		// Reset visual state after the panel slide-out transition (0.35s)
		setTimeout(() => {
			if (predictiveSearchDefault) {
				predictiveSearchDefault.classList.add('mm-none');
				predictiveSearchDefault.style.display = 'none';
			}
			const results = search.querySelector('#predictive-search');
			const empty = search.querySelector('#predictive-search-empty');
			if (results) results.style.display = 'none';
			if (empty) empty.style.display = 'none';
		}, 350);

		window.emit('global:search-closed', { keepFilterActive, keepStopScrolling });
	}
};

/*********************************************************************************************
 * 🌍 GLOBAL ACCESS
 */
window.SearchManager = SearchManager;

/*********************************************************************************************
 * 🔄 BACKWARD COMPATIBILITY
 */
window.mmShowSearch = (triggerElement) => SearchManager.show(triggerElement);
window.mmHideSearch = (keepFilter, keepScrolling) => SearchManager.hide(keepFilter, keepScrolling);

/*********************************************************************************************
 * 🎯 AUTO-INITIALIZATION
 */
document.addEventListener('DOMContentLoaded', () => {
	// Search close button (overlay close button)
	const searchCloseBtn = document.querySelector('[data-search-close]');
	if (searchCloseBtn) {
		searchCloseBtn.addEventListener('click', () => {
			SearchManager.hide();
		});
	}

	// Search clear button (inside search input)
	const searchClearBtn = document.querySelector('[data-search-clear]');
	if (searchClearBtn) {
		searchClearBtn.addEventListener('click', () => {
			const searchInput = document.getElementById('Search');
			const searchInputHeader = document.getElementById('SearchHeader');
			if (searchInput) {
				searchInput.value = '';
				searchInput.dispatchEvent(new Event('input'));
			}
			if (searchInputHeader) {
				searchInputHeader.value = '';
				searchInputHeader.dispatchEvent(new Event('input'));
			}
			SearchManager.hide();
		});
	}

	// Get search elements
	const searchInput = document.getElementById('Search');
	// Get all header search inputs (desktop and mobile)
	const searchInputHeaders = document.querySelectorAll('#SearchHeader, [data-search-input-header]');
	const searchInputHeader = searchInputHeaders.length > 0 ? searchInputHeaders[0] : null;
	const searchInputWrappers = document.querySelectorAll('.mm-search-input-wrapper');
	const searchInputWrapper = searchInputWrappers.length > 0 ? searchInputWrappers[0] : null;
	const predictiveSearchDefault = document.querySelector('#predictive-search-default');
	// Find the visible search container (mobile or desktop)
	let searchContainer = null;
	const allContainers = document.querySelectorAll('[data-search-container]');

	// If on mobile, prefer the container in .mm-mobile, otherwise use the first one
	if (window.innerWidth <= 768) {
		for (const container of allContainers) {
			if (container.closest('.mm-mobile') || !container.closest('.mm-desktop')) {
				searchContainer = container;
				break;
			}
		}
	}

	// If not found or on desktop, use the first container
	if (!searchContainer && allContainers.length > 0) {
		searchContainer = allContainers[0];
	}

	// Note: Removed automatic close on mouseleave to allow dev tools inspection
	// Overlay can be closed via:
	// - Close button (data-search-close)
	// - Escape key (handled in focus trap)
	// - Click outside (can be added if needed)

	// Open overlay when clicking on header search inputs (all instances: desktop and mobile)
	searchInputHeaders.forEach((input) => {
		if (!input.closest('[data-search-container]')) {
			input.addEventListener('focus', (e) => {
				SearchManager.show(e.target);
			});
			input.addEventListener('click', (e) => {
				e.stopPropagation();
				SearchManager.show(e.target);
			});
		}
	});

	// Also listen on all wrappers for better UX (only if not already inside overlay)
	searchInputWrappers.forEach((wrapper) => {
		if (!wrapper.closest('[data-search-container]')) {
			wrapper.addEventListener('click', (e) => {
				// Don't trigger if clicking directly on an input (already handled above)
				if (e.target.tagName === 'INPUT') return;

				// Find the input in this wrapper
				const wrapperInput = wrapper.querySelector('#SearchHeader') || wrapper.querySelector('[data-search-input-header]');
				if (wrapperInput) {
					// Trigger overlay and focus input
					SearchManager.show(wrapperInput);
					setTimeout(() => wrapperInput.focus(), 100);
				}
			});
		}
	});

	const syncInputsAndToggleDefault = (value) => {
		// Sync inputs
		if (searchInput && searchInputHeader) {
			searchInput.value = value;
			searchInputHeader.value = value;
		}

		// Show/hide default content
		if (predictiveSearchDefault) {
			if (value.trim() === '') {
				// Hide results immediately — don't wait for the 300ms debounce in PredictiveSearch
				const results = document.querySelector('#predictive-search');
				const empty = document.querySelector('#predictive-search-empty');
				if (results) results.style.display = 'none';
				if (empty) empty.style.display = 'none';

				predictiveSearchDefault.style.opacity = '0';
				predictiveSearchDefault.classList.remove('mm-none');
				predictiveSearchDefault.style.display = 'flex';
				predictiveSearchDefault.style.visibility = 'visible';
				requestAnimationFrame(() => {
					predictiveSearchDefault.style.opacity = '1';
				});
			} else {
				predictiveSearchDefault.style.opacity = '';
				predictiveSearchDefault.classList.add('mm-none');
				predictiveSearchDefault.style.display = 'none';
			}
		}
	};

	if (searchInput) {
		searchInput.addEventListener('input', (e) => {
			syncInputsAndToggleDefault(e.target.value);
		});
	}

	if (searchInputHeader) {
		searchInputHeader.addEventListener('input', (e) => {
			syncInputsAndToggleDefault(e.target.value);
		});
	}
});
